if (typeof WMainProductNew === 'undefined') {

class WMainProductNew {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.productId = container.dataset.productId;

    this.init();
  }

  init() {
    this.initGallery();
    this.initDeliveryRotation();
    this.bindVariantEvents();
    this.bindAccordionEvents();
    this.bindCrossSellEvents();
    this.bindDrawerEvents();
    this.initStickyBar();
  }

  initGallery() {
    const swiperEl = this.container.querySelector('.w-main-product-new__swiper');
    if (!swiperEl) return;

    this.loadSwiper().then(() => this.setupGallerySwiper());
  }

  loadSwiper() {
    if (window.Swiper) return Promise.resolve();

    return new Promise((resolve) => {
      if (!document.querySelector('link[href*="swiper"]')) {
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
        document.head.appendChild(css);
      }

      if (!document.querySelector('script[src*="swiper"]')) {
        const js = document.createElement('script');
        js.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
        js.onload = resolve;
        document.head.appendChild(js);
      } else {
        const check = setInterval(() => {
          if (window.Swiper) { clearInterval(check); resolve(); }
        }, 50);
      }
    });
  }

  setupGallerySwiper() {
    const swiperEl = this.container.querySelector('.w-main-product-new__swiper');
    const thumbnails = this.container.querySelectorAll('.w-main-product-new__thumbnail');
    const prevBtn = this.container.querySelector('.w-main-product-new__gallery-arrow--prev');
    const nextBtn = this.container.querySelector('.w-main-product-new__gallery-arrow--next');
    const dotsContainer = this.container.querySelector('.w-main-product-new__swiper-dots');
    const slideCount = swiperEl.querySelectorAll('.swiper-slide').length;

    if (dotsContainer) {
      for (let i = 0; i < slideCount; i++) {
        const dot = document.createElement('button');
        dot.className = 'swiper-dot' + (i === 0 ? ' swiper-dot--active' : '');
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dotsContainer.appendChild(dot);
      }
    }

    this.gallerySwiper = new Swiper(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      on: {
        slideChange: (swiper) => {
          const index = swiper.activeIndex;

          thumbnails.forEach((thumb, i) => {
            thumb.classList.toggle('w-main-product-new__thumbnail--active', i === index);
          });

          if (dotsContainer) {
            dotsContainer.querySelectorAll('.swiper-dot').forEach((d, i) => {
              d.classList.toggle('swiper-dot--active', i === index);
            });
          }

          if (prevBtn) prevBtn.style.opacity = index === 0 ? '0.5' : '1';
          if (nextBtn) nextBtn.style.opacity = index === slideCount - 1 ? '0.5' : '1';
        }
      }
    });

    if (prevBtn) prevBtn.style.opacity = '0.5';

    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.gallerySwiper.slideTo(index));
    });

    prevBtn?.addEventListener('click', () => this.gallerySwiper.slidePrev());
    nextBtn?.addEventListener('click', () => this.gallerySwiper.slideNext());

    if (dotsContainer) {
      dotsContainer.querySelectorAll('.swiper-dot').forEach((d, index) => {
        d.addEventListener('click', () => this.gallerySwiper.slideTo(index));
      });
    }
  }

  initDeliveryRotation() {
    const deliveryEl = this.container.querySelector('[data-delivery-messages]');
    if (!deliveryEl) return;

    const textEl = deliveryEl.querySelector('.w-main-product-new__delivery-text');
    const dots = deliveryEl.querySelectorAll('.w-main-product-new__delivery-dot');
    if (!textEl || dots.length <= 1) return;

    const messages = Array.from(dots).map((dot, i) => {
      return deliveryEl.dataset[`message${i}`] || textEl.textContent;
    });

    const interval = parseInt(deliveryEl.dataset.interval) || 3000;
    const fadeSpeed = parseInt(deliveryEl.dataset.fadeSpeed) || 300;

    deliveryEl.style.setProperty('--delivery-speed', `${fadeSpeed}ms`);

    let currentIndex = 0;

    const updateMessage = (index) => {
      textEl.classList.add('fade-out');

      setTimeout(() => {
        textEl.textContent = messages[index];
        dots.forEach((dot, i) => {
          dot.classList.toggle('w-main-product-new__delivery-dot--active', i === index);
        });
        textEl.classList.remove('fade-out');
        textEl.classList.add('fade-in');

        setTimeout(() => textEl.classList.remove('fade-in'), fadeSpeed);
      }, fadeSpeed);
    };

    setInterval(() => {
      currentIndex = (currentIndex + 1) % messages.length;
      updateMessage(currentIndex);
    }, interval);
  }

  bindVariantEvents() {
    const variantsWrapper = this.container.querySelector('[data-variants-wrapper]');
    if (!variantsWrapper) return;

    const variantsJsonEl = variantsWrapper.querySelector('[data-variants-json]');
    if (!variantsJsonEl) {

      this.bindLegacyVariantEvents();
      return;
    }

    let variantData;
    try {
      variantData = JSON.parse(variantsJsonEl.textContent);
    } catch (e) {
      this.bindLegacyVariantEvents();
      return;
    }

    this.variantData = variantData;
    this.selectedOptions = this.getInitialSelectedOptions();

    const optionButtons = variantsWrapper.querySelectorAll('.w-main-product-new__option-btn');
    optionButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const optionIndex = parseInt(btn.dataset.optionIndex, 10);
        const optionValue = btn.dataset.optionValue;

        const parentGroup = btn.closest('.w-main-product-new__option-group');
        parentGroup.querySelectorAll('.w-main-product-new__option-btn').forEach(b => {
          b.classList.remove('w-main-product-new__option-btn--selected');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('w-main-product-new__option-btn--selected');
        btn.setAttribute('aria-pressed', 'true');

        const selectedValueEl = parentGroup.querySelector('[data-selected-value]');
        if (selectedValueEl) {
          selectedValueEl.textContent = optionValue;
        }

        this.selectedOptions[optionIndex] = optionValue;
        this.updateVariantFromOptions();
      });
    });

    const variantInputs = variantsWrapper.querySelectorAll('.w-main-product-new__variant-input');
    variantInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        const optionIndex = parseInt(input.dataset.optionIndex, 10);
        const optionValue = e.target.value;
        const variantCard = e.target.closest('.w-main-product-new__variant');

        const parentGroup = input.closest('.w-main-product-new__option-group');
        parentGroup.querySelectorAll('.w-main-product-new__variant').forEach(card => {
          card.classList.remove('w-main-product-new__variant--selected');
        });
        variantCard?.classList.add('w-main-product-new__variant--selected');

        this.selectedOptions[optionIndex] = optionValue;
        this.updateVariantFromOptions();
      });
    });
  }

  getInitialSelectedOptions() {
    const options = [];
    const optionGroups = this.container.querySelectorAll('[data-option-index]');
    const processedIndices = new Set();

    optionGroups.forEach(group => {
      const index = parseInt(group.dataset.optionIndex, 10);
      if (processedIndices.has(index)) return;

      const selectedBtn = group.querySelector('.w-main-product-new__option-btn--selected');
      if (selectedBtn) {
        options[index] = selectedBtn.dataset.optionValue;
        processedIndices.add(index);
        return;
      }

      const checkedInput = group.querySelector('.w-main-product-new__variant-input:checked');
      if (checkedInput) {
        options[index] = checkedInput.value;
        processedIndices.add(index);
      }
    });

    return options;
  }

  findVariantByOptions(selectedOptions) {
    if (!this.variantData || !this.variantData.variants) return null;

    return this.variantData.variants.find(variant => {
      return variant.options.every((opt, index) => opt === selectedOptions[index]);
    });
  }

  updateVariantFromOptions() {
    const variant = this.findVariantByOptions(this.selectedOptions);
    if (!variant) return;

    const variantId = variant.id.toString();

    const form = this.container.querySelector('.w-main-product-new__form');
    if (form) {
      const hiddenInput = form.querySelector('input[name="id"]');
      if (hiddenInput) hiddenInput.value = variantId;
    }

    const stickyVariantInput = this.container.querySelector('[data-sticky-variant-id]');
    if (stickyVariantInput) stickyVariantInput.value = variantId;

    this.updatePriceDisplay(variant);

    const kitWrapper = this.container.querySelector('[data-kit-wrapper]');
    if (kitWrapper) {
      const allKits = kitWrapper.querySelectorAll('[data-variant-kit]');
      allKits.forEach(kit => {
        if (kit.dataset.variantKit === variantId) {
          kit.removeAttribute('hidden');
        } else {
          kit.setAttribute('hidden', '');
        }
      });
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }

  updatePriceDisplay(variant) {
    const ctaPrice = this.container.querySelector('.w-main-product-new__cta-price');
    const ctaPriceCrossed = this.container.querySelector('.w-main-product-new__cta-price-crossed');
    const ctaPriceCrossedText = this.container.querySelector('.w-main-product-new__cta-price-crossed-text');
    const stickyPrice = this.container.querySelector('[data-sticky-price]');
    const packPriceCurrent = this.container.querySelector('.w-main-product-new__pack-price-current');
    const packPriceCompare = this.container.querySelector('.w-main-product-new__pack-price-compare');
    const packPriceBadge = this.container.querySelector('.w-main-product-new__pack-price-badge');

    const formatPrice = (cents) => {
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    };

    const priceFormatted = formatPrice(variant.price);
    const hasDiscount = variant.compare_at_price > variant.price;
    const compareFormatted = hasDiscount ? formatPrice(variant.compare_at_price) : null;

    if (ctaPrice) ctaPrice.textContent = priceFormatted;
    if (stickyPrice) stickyPrice.textContent = priceFormatted;
    if (packPriceCurrent) packPriceCurrent.textContent = priceFormatted;

    if (ctaPriceCrossed) {
      if (hasDiscount) {
        if (ctaPriceCrossedText) ctaPriceCrossedText.textContent = compareFormatted;
        ctaPriceCrossed.style.display = 'flex';
      } else {
        ctaPriceCrossed.style.display = 'none';
      }
    }

    if (packPriceCompare) {
      if (hasDiscount) {
        packPriceCompare.textContent = compareFormatted;
        packPriceCompare.style.display = '';
        if (packPriceBadge) packPriceBadge.style.display = '';
      } else {
        packPriceCompare.style.display = 'none';
        if (packPriceBadge) packPriceBadge.style.display = 'none';
      }
    }
  }

  bindLegacyVariantEvents() {
    const variantInputs = this.container.querySelectorAll('.w-main-product-new__variant-input');
    const form = this.container.querySelector('.w-main-product-new__form');
    const ctaPrice = this.container.querySelector('.w-main-product-new__cta-price');
    const ctaPriceCrossed = this.container.querySelector('.w-main-product-new__cta-price-crossed');
    const ctaPriceCrossedText = this.container.querySelector('.w-main-product-new__cta-price-crossed-text');
    const stickyVariantInput = this.container.querySelector('[data-sticky-variant-id]');
    const stickyPrice = this.container.querySelector('[data-sticky-price]');

    variantInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        const variantId = e.target.value;
        const variantCard = e.target.closest('.w-main-product-new__variant');

        this.container.querySelectorAll('.w-main-product-new__variant').forEach(card => {
          card.classList.remove('w-main-product-new__variant--selected');
        });

        variantCard?.classList.add('w-main-product-new__variant--selected');

        if (form) {
          const hiddenInput = form.querySelector('input[name="id"]');
          if (hiddenInput) hiddenInput.value = variantId;
        }

        if (stickyVariantInput) stickyVariantInput.value = variantId;

        const priceEl = variantCard?.querySelector('.w-main-product-new__variant-price');
        const priceOriginalEl = variantCard?.querySelector('.w-main-product-new__variant-price-original');

        if (priceEl && ctaPrice) {
          ctaPrice.textContent = priceEl.textContent;
        }

        if (priceEl && stickyPrice) {
          stickyPrice.textContent = priceEl.textContent;
        }

        if (priceOriginalEl && ctaPriceCrossed) {
          if (ctaPriceCrossedText) {
            ctaPriceCrossedText.textContent = priceOriginalEl.textContent;
          }
          ctaPriceCrossed.style.display = 'flex';
        } else if (ctaPriceCrossed) {
          ctaPriceCrossed.style.display = 'none';
        }

        const packPriceCurrent = this.container.querySelector('.w-main-product-new__pack-price-current');
        const packPriceCompare = this.container.querySelector('.w-main-product-new__pack-price-compare');
        const packPriceBadge = this.container.querySelector('.w-main-product-new__pack-price-badge');
        if (packPriceCurrent && priceEl) {
          packPriceCurrent.textContent = priceEl.textContent;
        }
        if (packPriceCompare && priceOriginalEl) {
          packPriceCompare.textContent = priceOriginalEl.textContent;
          if (packPriceCompare) packPriceCompare.style.display = '';
          if (packPriceBadge) packPriceBadge.style.display = '';
        } else if (packPriceCompare) {
          packPriceCompare.style.display = 'none';
          if (packPriceBadge) packPriceBadge.style.display = 'none';
        }

        const kitWrapper = this.container.querySelector('[data-kit-wrapper]');
        if (kitWrapper) {
          const allKits = kitWrapper.querySelectorAll('[data-variant-kit]');
          allKits.forEach(kit => {
            if (kit.dataset.variantKit === variantId) {
              kit.removeAttribute('hidden');
            } else {
              kit.setAttribute('hidden', '');
            }
          });
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url);
      });
    });
  }

  bindAccordionEvents() {
    const accordions = this.container.querySelectorAll('[data-accordion]');
    const animationDuration = 300;

    accordions.forEach(accordion => {
      const header = accordion.querySelector('.w-main-product-new__accordion-header');
      const body = accordion.querySelector('.w-main-product-new__accordion-body');

      if (body && !accordion.hasAttribute('data-open')) {
        body.style.height = '0';
      }

      header?.addEventListener('click', () => {
        const isOpen = accordion.hasAttribute('data-open');

        accordions.forEach(other => {
          if (other !== accordion && other.hasAttribute('data-open')) {
            const otherBody = other.querySelector('.w-main-product-new__accordion-body');
            const otherHeader = other.querySelector('.w-main-product-new__accordion-header');

            if (otherBody) {
              otherBody.style.height = otherBody.scrollHeight + 'px';
              requestAnimationFrame(() => {
                otherBody.style.height = '0';
              });
            }
            other.removeAttribute('data-open');
            otherHeader?.setAttribute('aria-expanded', 'false');
          }
        });

        if (isOpen) {

          if (body) {
            body.style.height = body.scrollHeight + 'px';
            requestAnimationFrame(() => {
              body.style.height = '0';
            });
          }
          accordion.removeAttribute('data-open');
          header.setAttribute('aria-expanded', 'false');
        } else {

          if (body) {
            body.style.height = '0';
            body.removeAttribute('hidden');
            requestAnimationFrame(() => {
              accordion.setAttribute('data-open', '');
              body.style.height = body.scrollHeight + 'px';
              setTimeout(() => {
                body.style.height = 'auto';
              }, animationDuration);
            });
          }
          header.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  bindCrossSellEvents() {
    const crossSellInputs = this.container.querySelectorAll('.w-main-product-new__cross-sell-input');
    const totalEl = this.container.querySelector('.w-main-product-new__cross-sell-total');
    const crossedPriceWrapper = this.container.querySelector('#add-cross-sell .w-main-product-new__cta-price-crossed');
    const crossedPriceText = this.container.querySelector('#add-cross-sell .w-main-product-new__cta-price-crossed-text');
    const addButton = this.container.querySelector('#add-cross-sell');

    if (!crossSellInputs.length || !totalEl) return;

    const updateTotal = () => {
      let total = 0;
      let compareTotal = 0;
      crossSellInputs.forEach(input => {
        if (input.checked && input.dataset.price) {
          total += parseInt(input.dataset.price, 10);
          compareTotal += parseInt(input.dataset.comparePrice || input.dataset.price, 10);
        }
      });

      const formattedTotal = (total / 100).toFixed(2).replace('.', ',') + ' €';
      totalEl.textContent = formattedTotal;

      if (crossedPriceWrapper && crossedPriceText) {
        if (compareTotal > total) {
          const formattedCompare = (compareTotal / 100).toFixed(2).replace('.', ',') + ' €';
          crossedPriceText.textContent = formattedCompare;
          crossedPriceWrapper.style.display = '';
        } else {
          crossedPriceWrapper.style.display = 'none';
        }
      }

      if (addButton) {
        addButton.disabled = total === 0;
      }
    };

    crossSellInputs.forEach(input => {
      input.addEventListener('change', updateTotal);
    });

    updateTotal();

    if (addButton) {
      addButton.addEventListener('click', async (e) => {
        e.preventDefault();

        const checkedInputs = Array.from(crossSellInputs).filter(input => input.checked && input.value);
        if (checkedInputs.length === 0) return;

        addButton.disabled = true;
        addButton.classList.add('is-loading');

        try {
          for (const input of checkedInputs) {
            const variantId = input.value;
            if (!variantId) continue;

            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1 })
            });
          }

          const sidecart = document.querySelector('w-sidecart');
          if (sidecart) {
            await sidecart.refreshCart();
            sidecart.open();
          } else {
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          }

          checkedInputs.forEach(input => {
            input.checked = false;
          });
          updateTotal();

        } catch (error) {
          console.error('Cross-sell add to cart error:', error);
        } finally {
          addButton.disabled = false;
          addButton.classList.remove('is-loading');
        }
      });
    }
  }

  bindDrawerEvents() {
    const overlay = this.container.querySelector('[data-drawer-overlay]');
    const openButtons = this.container.querySelectorAll('[data-drawer-open]');
    const closeButtons = this.container.querySelectorAll('[data-drawer-close]');
    const drawers = this.container.querySelectorAll('[data-drawer]');

    const openDrawer = (drawerId) => {
      const drawer = this.container.querySelector(`[data-drawer="${drawerId}"]`);
      if (drawer && overlay) {
        drawer.removeAttribute('hidden');
        overlay.removeAttribute('hidden');
        requestAnimationFrame(() => {
          drawer.setAttribute('data-open', '');
          overlay.setAttribute('data-open', '');
        });
      }
    };

    const closeDrawer = () => {
      drawers.forEach(drawer => {
        drawer.removeAttribute('data-open');
        setTimeout(() => drawer.setAttribute('hidden', ''), 300);
      });
      if (overlay) {
        overlay.removeAttribute('data-open');
        setTimeout(() => overlay.setAttribute('hidden', ''), 300);
      }
    };

    openButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const drawerId = btn.dataset.drawerOpen;
        openDrawer(drawerId);
      });
    });

    closeButtons.forEach(btn => {
      btn.addEventListener('click', closeDrawer);
    });

    if (overlay) {
      overlay.addEventListener('click', closeDrawer);
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeDrawer();
    });
  }

  initStickyBar() {
    const stickyBar = this.container.querySelector('[data-sticky-bar]');
    const ctaButton = this.container.querySelector('.w-main-product-new__cta-button');

    if (!stickyBar || !ctaButton) return;

    const checkVisibility = () => {
      const scrollY = window.scrollY;
      const ctaRect = ctaButton.getBoundingClientRect();
      const ctaVisible = ctaRect.top < window.innerHeight && ctaRect.bottom > 0;

      if (!ctaVisible) {
        stickyBar.setAttribute('data-visible', '');
      } else {
        stickyBar.removeAttribute('data-visible');
      }
    };

    window.addEventListener('scroll', checkVisibility, { passive: true });
    window.addEventListener('resize', checkVisibility, { passive: true });
    checkVisibility();

    this.stickyBarCleanup = () => {
      window.removeEventListener('scroll', checkVisibility);
      window.removeEventListener('resize', checkVisibility);
    };
  }

  destroy() {
    if (this.stickyBarCleanup) this.stickyBarCleanup();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSections);
} else {
  initSections();
}

function initSections() {
  const sections = document.querySelectorAll('.w-main-product-new');
  sections.forEach(section => {
    new WMainProductNew(section);
  });
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('.w-main-product-new');
  if (section) {
    new WMainProductNew(section);
  }
});

}
